import { ChevronDown, ChevronUp, FileJson, Plus, Upload } from 'lucide-react'
import { XeroImportForm, XeroManualForm } from '../../components/licensing'
import type { ImportResponse } from '../../types'

export type ImportMode = 'none' | 'json' | 'manual'

interface ImportXeroSectionProps {
  show: boolean
  toggleShow: () => void
  importMode: ImportMode
  setImportMode: (mode: ImportMode) => void
  onImportSuccess: (result: ImportResponse) => void
  onImportError: (error: Error) => void
  onCreateOrganisation: (xeroOrgId?: string) => void
}

interface ImportModeButtonProps {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}

function ImportModeButton({
  active,
  onClick,
  icon,
  label,
}: ImportModeButtonProps): React.ReactElement {
  const className = active
    ? 'bg-[#5c6e94] text-white'
    : 'border border-stone-300 dark:border-stone-600 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700'
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${className}`}
    >
      {icon}
      {label}
    </button>
  )
}

interface ImportFormsProps {
  importMode: ImportMode
  onImportSuccess: (result: ImportResponse) => void
  onImportError: (error: Error) => void
  onCreateOrganisation: (xeroOrgId?: string) => void
}

function ImportForms(props: ImportFormsProps): React.ReactElement | null {
  const { importMode, onImportSuccess, onImportError, onCreateOrganisation } = props
  if (importMode === 'json') {
    return <XeroImportForm onSuccess={onImportSuccess} onError={onImportError} />
  }
  if (importMode === 'manual') {
    return (
      <XeroManualForm
        onSuccess={onImportSuccess}
        onError={onImportError}
        onCreateOrganisation={onCreateOrganisation}
      />
    )
  }
  return null
}

function ImportSectionHeader({
  show,
  toggleShow,
}: {
  show: boolean
  toggleShow: () => void
}): React.ReactElement {
  return (
    <button
      onClick={toggleShow}
      className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-stone-50 dark:hover:bg-stone-700/50"
    >
      <div className="flex items-center gap-3">
        <Upload className="h-5 w-5 text-stone-500 dark:text-stone-400" />
        <div>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">Import Xero Data</h2>
          <p className="text-sm text-stone-600 dark:text-stone-400">
            Import invoice data from JSON file or enter manually
          </p>
        </div>
      </div>
      {show ? (
        <ChevronUp className="h-5 w-5 text-stone-400" />
      ) : (
        <ChevronDown className="h-5 w-5 text-stone-400" />
      )}
    </button>
  )
}

export function ImportXeroSection(props: ImportXeroSectionProps): React.ReactElement {
  const { show, toggleShow, importMode, setImportMode } = props
  const { onImportSuccess, onImportError, onCreateOrganisation } = props
  const toggleMode = (mode: 'json' | 'manual'): void => {
    setImportMode(importMode === mode ? 'none' : mode)
  }
  return (
    <div className="overflow-hidden rounded-xl border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-800">
      <ImportSectionHeader show={show} toggleShow={toggleShow} />
      {show && (
        <div className="border-t border-stone-200 px-6 pb-6 dark:border-stone-700">
          <div className="flex gap-3 pt-4 pb-4">
            <ImportModeButton
              active={importMode === 'json'}
              onClick={() => {
                toggleMode('json')
              }}
              icon={<FileJson className="h-4 w-4" />}
              label="Import JSON"
            />
            <ImportModeButton
              active={importMode === 'manual'}
              onClick={() => {
                toggleMode('manual')
              }}
              icon={<Plus className="h-4 w-4" />}
              label="Manual Entry"
            />
          </div>
          <ImportForms
            importMode={importMode}
            onImportSuccess={onImportSuccess}
            onImportError={onImportError}
            onCreateOrganisation={onCreateOrganisation}
          />
        </div>
      )}
    </div>
  )
}
